<template>
  <ReportCard :title="$t('report.members_explore_title')" header-margin="mb-1">
    <template #header>
      <span class="px-1.5 py-0.5 rounded-full text-[8px] font-bold uppercase tracking-wider bg-amber-500/15 text-amber-600 dark:text-amber-400 border border-amber-500/25">
        {{ $t('common.experimental') }}
      </span>
    </template>
    <p class="text-[10px] text-muted-foreground mb-4 ml-3">
      {{ $t('report.members_explore_description') }}
    </p>

    <!-- Dimension Picker -->
    <div class="mb-3">
      <label class="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5 block">
        {{ $t('report.members_dimension_label') }} (max 2)
      </label>
      <div class="flex flex-wrap gap-1.5">
        <button v-for="dim in availableDimensions" :key="dim.value"
          @click="toggleDimension(dim.value)"
          class="px-2.5 py-1 rounded-full text-[10px] font-semibold transition-all border"
          :class="selectedDimensions.includes(dim.value)
            ? 'bg-cyan-500/15 border-cyan-500/40 text-cyan-600 dark:text-cyan-400'
            : 'bg-transparent border-gray-200 dark:border-gray-700 text-muted-foreground hover:border-gray-300'">
          {{ dim.label }}
        </button>
      </div>
    </div>

    <!-- Metrics Picker -->
    <div class="mb-4">
      <label class="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5 block">
        {{ $t('report.members_metrics_label') }}
      </label>
      <div class="flex flex-wrap gap-1.5">
        <button v-for="met in availableMetrics" :key="met.value"
          @click="toggleMetric(met.value)"
          class="px-2.5 py-1 rounded-full text-[10px] font-semibold transition-all border"
          :class="selectedMetrics.includes(met.value)
            ? 'bg-cyan-500/15 border-cyan-500/40 text-cyan-600 dark:text-cyan-400'
            : 'bg-transparent border-gray-200 dark:border-gray-700 text-muted-foreground hover:border-gray-300'">
          {{ met.label }}
        </button>
      </div>
    </div>

    <!-- Generate Button -->
    <button @click="generateExplorer"
      :disabled="selectedDimensions.length === 0 || selectedMetrics.length === 0 || explorerLoading"
      class="w-full py-2.5 rounded-lg text-[11px] font-bold uppercase tracking-wider transition-all"
      :class="selectedDimensions.length > 0 && selectedMetrics.length > 0
        ? 'bg-cyan-500 text-white hover:bg-cyan-600 active:bg-cyan-700'
        : 'bg-gray-100 dark:bg-gray-800 text-muted-foreground cursor-not-allowed'">
      <span v-if="explorerLoading" class="flex items-center justify-center gap-2">
        <svg class="animate-spin h-3.5 w-3.5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
        </svg>
        {{ $t('report.members_generate') }}
      </span>
      <span v-else>{{ $t('report.members_generate') }}</span>
    </button>

    <!-- Explorer Result -->
    <div v-if="explorerResponse" class="mt-4">
      <div ref="explorerChartEl" class="w-full" style="height: 300px"></div>

      <!-- Data Table -->
      <div v-if="explorerResponse.data.length > 0" class="mt-3 overflow-x-auto">
        <table class="w-full text-[10px]">
          <thead>
            <tr class="border-b border-gray-200 dark:border-gray-700">
              <th v-for="dim in explorerResponse.metadata.dimensions" :key="dim"
                class="text-left py-1.5 px-2 font-semibold uppercase tracking-wider text-muted-foreground">
                {{ $t('report.members_dim_' + dim) }}
              </th>
              <th v-for="met in explorerResponse.metadata.metrics" :key="met"
                class="text-right py-1.5 px-2 font-semibold uppercase tracking-wider text-muted-foreground">
                {{ $t('report.members_met_' + met) }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, i) in explorerTableData" :key="i"
              class="border-b border-gray-100 dark:border-gray-800">
              <td v-for="dim in explorerResponse.metadata.dimensions" :key="dim"
                class="py-1.5 px-2 text-foreground">
                {{ row.dimensions[dim] ?? '\u2014' }}
              </td>
              <td v-for="met in explorerResponse.metadata.metrics" :key="met"
                class="py-1.5 px-2 text-right font-medium text-foreground">
                {{ row.metrics[met as MemberMetric] != null ? formatMetricValue(met, row.metrics[met as MemberMetric] ?? null) : '\u2014' }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </ReportCard>
</template>

<script setup lang="ts">
import { computed, ref, shallowRef, onBeforeUnmount, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import * as echarts from 'echarts'
import { DateTime } from 'luxon'
import { MembersApi } from '../../api/MembersApi'
import type { MembersStatsResponse, MemberDimension, MemberMetric } from '../../responses'
import ReportCard from '../ReportCard.vue'

const props = defineProps<{
  centerId: number
  startDate: Date
  endDate: Date
}>()

const { t } = useI18n()

const selectedDimensions = ref<MemberDimension[]>([])
const selectedMetrics = ref<MemberMetric[]>(['count'])
const explorerLoading = ref(false)
const explorerResponse = ref<MembersStatsResponse | null>(null)
const explorerChartEl = ref<HTMLElement>()
const explorerChartInstance = shallowRef<echarts.ECharts>()

const availableDimensions = computed(() => {
  const dims: MemberDimension[] = [
    'type', 'card_type', 'active', 'age_range',
    'registration_year', 'registration_month',
    'contactability', 'zipcode',
    'purchase_bracket', 'recency', 'favorite_family',
    'favorite_day', 'favorite_time_slot', 'visit_frequency',
    'first_activity',
  ]
  return dims.map(d => ({ value: d, label: t('report.members_dim_' + d) }))
})

const availableMetrics = computed(() => {
  const mets: MemberMetric[] = [
    'count', 'avg_purchases', 'avg_basket',
    'avg_days_between_visits', 'avg_lifetime_days',
  ]
  return mets.map(m => ({ value: m, label: t('report.members_met_' + m) }))
})

function toggleDimension(dim: MemberDimension) {
  const idx = selectedDimensions.value.indexOf(dim)
  if (idx >= 0) {
    selectedDimensions.value.splice(idx, 1)
  } else if (selectedDimensions.value.length < 2) {
    selectedDimensions.value.push(dim)
  }
}

function toggleMetric(met: MemberMetric) {
  const idx = selectedMetrics.value.indexOf(met)
  if (idx >= 0 && selectedMetrics.value.length > 1) {
    selectedMetrics.value.splice(idx, 1)
  } else if (idx < 0) {
    selectedMetrics.value.push(met)
  }
}

async function generateExplorer() {
  if (selectedDimensions.value.length === 0 || selectedMetrics.value.length === 0) return
  explorerLoading.value = true
  try {
    explorerResponse.value = await MembersApi.fetchMembersStats(
      DateTime.fromJSDate(props.startDate),
      DateTime.fromJSDate(props.endDate),
      'month',
      props.centerId,
      [...selectedDimensions.value],
      [...selectedMetrics.value],
    )
    await nextTick()
    buildExplorerChart()
  } catch {
    // Silent
  } finally {
    explorerLoading.value = false
  }
}

const explorerTableData = computed(() => {
  if (!explorerResponse.value?.data) return []
  const firstMetric = explorerResponse.value.metadata.metrics[0] as MemberMetric
  return [...explorerResponse.value.data].sort((a, b) => {
    return (b.metrics[firstMetric] ?? 0) - (a.metrics[firstMetric] ?? 0)
  })
})

function formatMetricValue(metric: string, value: number | null): string {
  if (value == null) return '\u2014'
  if (metric === 'count') return value.toLocaleString()
  if (metric.startsWith('avg_')) return value.toFixed(1)
  return value.toString()
}

function buildExplorerChart() {
  if (!explorerChartEl.value || !explorerResponse.value) return

  if (!explorerChartInstance.value) {
    explorerChartInstance.value = echarts.init(explorerChartEl.value)
  }

  const resp = explorerResponse.value
  const dims = resp.metadata.dimensions
  const mets = resp.metadata.metrics
  const data = resp.data

  const colors = ['#06C8FF', '#10B981', '#F59E0B', '#8B5CF6', '#EF4444', '#EC4899', '#14B8A6', '#F97316']

  if (dims.length === 1 && mets.length === 1) {
    const sorted = [...data].sort((a, b) => (b.metrics[mets[0] as MemberMetric] ?? 0) - (a.metrics[mets[0] as MemberMetric] ?? 0))
    const categories = sorted.map(d => d.dimensions[dims[0]] ?? '\u2014')
    const values = sorted.map(d => d.metrics[mets[0] as MemberMetric] ?? 0)

    explorerChartInstance.value.setOption({
      tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
      grid: { left: '3%', right: '4%', bottom: '8%', top: '6%', containLabel: true },
      xAxis: { type: 'category', data: categories, axisLabel: { fontSize: 9, rotate: categories.length > 6 ? 30 : 0 } },
      yAxis: { type: 'value', axisLabel: { fontSize: 9 } },
      series: [{ type: 'bar', data: values, itemStyle: { color: '#06C8FF', borderRadius: [3, 3, 0, 0] } }],
    }, true)
  } else if (dims.length === 1 && mets.length >= 2) {
    const sorted = [...data].sort((a, b) => (b.metrics[mets[0] as MemberMetric] ?? 0) - (a.metrics[mets[0] as MemberMetric] ?? 0))
    const categories = sorted.map(d => d.dimensions[dims[0]] ?? '\u2014')

    explorerChartInstance.value.setOption({
      tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
      legend: { data: mets.map(m => t('report.members_met_' + m)), bottom: 0, textStyle: { fontSize: 9 } },
      grid: { left: '3%', right: '4%', bottom: '14%', top: '6%', containLabel: true },
      xAxis: { type: 'category', data: categories, axisLabel: { fontSize: 9, rotate: categories.length > 6 ? 30 : 0 } },
      yAxis: { type: 'value', axisLabel: { fontSize: 9 } },
      series: mets.map((m, i) => ({
        name: t('report.members_met_' + m),
        type: 'bar',
        data: sorted.map(d => d.metrics[m as MemberMetric] ?? 0),
        itemStyle: { color: colors[i % colors.length], borderRadius: [3, 3, 0, 0] },
      })),
    }, true)
  } else if (dims.length === 2 && mets.length >= 1) {
    const met = mets[0] as MemberMetric
    const dim1Values = [...new Set(data.map(d => d.dimensions[dims[0]]))]
    const dim2Values = [...new Set(data.map(d => d.dimensions[dims[1]]))]

    const dataMap = new Map<string, number>()
    for (const d of data) {
      dataMap.set(`${d.dimensions[dims[0]]}|${d.dimensions[dims[1]]}`, d.metrics[met] ?? 0)
    }

    explorerChartInstance.value.setOption({
      tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
      legend: { data: dim2Values, bottom: 0, textStyle: { fontSize: 9 } },
      grid: { left: '3%', right: '4%', bottom: '14%', top: '6%', containLabel: true },
      xAxis: { type: 'category', data: dim1Values, axisLabel: { fontSize: 9, rotate: dim1Values.length > 6 ? 30 : 0 } },
      yAxis: { type: 'value', axisLabel: { fontSize: 9 } },
      series: dim2Values.map((d2, i) => ({
        name: d2,
        type: 'bar',
        stack: 'total',
        data: dim1Values.map(d1 => dataMap.get(`${d1}|${d2}`) ?? 0),
        itemStyle: { color: colors[i % colors.length] },
      })),
    }, true)
  }
}

onBeforeUnmount(() => {
  explorerChartInstance.value?.dispose()
})
</script>
